import { defineMessages, useIntl } from "react-intl";

import { DEFAULT_VALUES, useLocalizedHref } from "@/libraries/web-framework";
import FaqAccordions from "@/patterns/wpl/faq-accordions/src";
import type { Feature } from "@/patterns/wpl/faq-accordions/src";

const localizedIntlLink = (chunks: React.ReactNode, href: string) => (
    // eslint-disable-next-line react-hooks/rules-of-hooks
    <a href={useLocalizedHref(href)}>{chunks}</a>
);

const sharedRichtextValues = {
    ...DEFAULT_VALUES,
    p: (chunks: React.ReactNode) => <p>{chunks}</p>,
    strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
    ul: (chunks: React.ReactNode) => <ul>{chunks}</ul>,
    li: (chunks: React.ReactNode) => <li>{chunks}</li>,
    pricing: (chunks: React.ReactNode) => localizedIntlLink(chunks, "#"),
    standardCost: (chunks: React.ReactNode) =>
        localizedIntlLink(
            chunks,
            "/licensing/confluence#what-does-a-confluence-cloud-standard-subscription-cost"
        ),
    premiumCost: (chunks: React.ReactNode) =>
        localizedIntlLink(
            chunks,
            "/licensing/confluence#what-does-a-confluence-cloud-premium-subscription-cost"
        ),
    contact: (chunks: React.ReactNode) =>
        localizedIntlLink(
            chunks,
            "/enterprise/contact?formType=pricing-quotes"
        ),
    refund: (chunks: React.ReactNode) =>
        localizedIntlLink(
            chunks,
            "/licensing/cloud#what-is-atlassian-refund-policy"
        ),
    premium: (chunks: React.ReactNode) =>
        localizedIntlLink(chunks, "/software/confluence/premium"),
    journeyToCloud: (chunks: React.ReactNode) =>
        localizedIntlLink(chunks, "/migration/assess/journey-to-cloud"),
    compareCloud: (chunks: React.ReactNode) =>
        localizedIntlLink(
            chunks,
            "/migration/assess/compare-cloud-data-center"
        ),
    migrationAssessment: (chunks: React.ReactNode) =>
        localizedIntlLink(chunks, "/migration/assess/migration-assessment"),
    access: (chunks: React.ReactNode) =>
        localizedIntlLink(chunks, "/software/access"),
    atlassianGuardStandard: (chunks: React.ReactNode) =>
        localizedIntlLink(chunks, "/software/access"),
    atlassianGuardPremium: (chunks: React.ReactNode) =>
        localizedIntlLink(chunks, "/software/access"),
    downloadConfluence: (chunks: React.ReactNode) =>
        localizedIntlLink(chunks, "/software/confluence/download/data-center"),
    purchaseConfluenceDataCenter: (chunks: React.ReactNode) =>
        localizedIntlLink(chunks, "/purchase/product/confluence-data-center"),
    licenseDatacenter: (chunks: React.ReactNode) =>
        localizedIntlLink(chunks, "/licensing/data-center"),
    communityLicense: (chunks: React.ReactNode) =>
        localizedIntlLink(chunks, "/software/views/community-license-request")
};

export const CloudPricingFaq = () => {
    const intl = useIntl();

    const messages = defineMessages({
        faqHeader: {
            id: "ConfluencePricingPage.cloud.faqHeader",
            defaultMessage: "Get more details"
        },
        calculateCostHeader: {
            id: "ConfluencePricingPage.cloud.calculateCostHeader",
            defaultMessage: "How to calculate your subscription cost"
        },
        calculateCostContent: {
            id: "ConfluencePricingPage.cloud.calculateCostContent",
            defaultMessage:
                "<p>Use our <pricing>pricing calculator</pricing> at the top of this page to determine the price of your subscription. Simply enter how many users you have and choose a billing cycle (annual or monthly).</p><p>Our detailed cloud pricing tables include price ranges for our <standardCost>Standard</standardCost> or <premiumCost>Premium</premiumCost> plans on our purchasing and licensing site. <contact>Contact our sales team</contact> for Enterprise pricing, available only for annual subscriptions.</p>"
        },
        aboutBillingHeader: {
            id: "ConfluencePricingPage.cloud.aboutBillingHeader",
            defaultMessage: "About users & how you're billed for them"
        },
        aboutBillingContent: {
            id: "ConfluencePricingPage.cloud.aboutBillingContent",
            defaultMessage:
                "<p>A <strong>user</strong> is someone who can log in to one of your Confluence sites and who exists in User Management. You can add and remove users as your team changes.</p><p>For <strong>monthly</strong> subscriptions, we'll charge you for the following month's subscription based on the exact number of Confluence users you have.</p><p>For <strong>annual</strong> subscriptions, we'll charge you for the tier that most closely matches your user count. Depending on the number of users purchased, annual subscriptions may be a better value.</p><p>See monthly and annual tiers for our <standardCost>Standard</standardCost> or <premiumCost>Premium</premiumCost> plan.</p>"
        },
        paymentHeader: {
            id: "ConfluencePricingPage.cloud.paymentHeader",
            defaultMessage: "Payment options"
        },
        paymentContent: {
            id: "ConfluencePricingPage.cloud.paymentContent",
            defaultMessage:
                "<p>You can pay <strong>monthly subscriptions</strong> by credit card (MasterCard, Visa, or American Express) or PayPal.</p> <p>For <strong>annual subscriptions</strong>, you can pay by credit card, bank transfer, or check.</p>"
        },
        freeTrialPeriodHeader: {
            id: "ConfluencePricingPage.cloud.freeTrialPeriodHeader",
            defaultMessage:
                "Free trial period, refunds, & subscription plan details"
        },
        freeTrialPeriodContent: {
            id: "ConfluencePricingPage.cloud.freeTrialPeriodContent",
            defaultMessage:
                "<p>Our <strong>Free plan</strong> lets you try Confluence for up to 10 users with 2GB of storage and Community Support.</p> <p>If you'd like to add more than 10 users or get access to more support and storage, you can sign up for a <strong>7-day free trial</strong> of our Standard or Premium plan. If you're on the Free plan and exceed the user limit, we'll automatically upgrade you to a 7-day free trial of the Standard plan.</p> <p>Cancel any time, no payment information required.</p> <p>We also offer <strong>annual subscriptions</strong> for Confluence. For these subscriptions, we'll bill you for the tier most closely matching your user count. Depending on the number of users purchased, annual subscriptions may be a better value.</p> <p>If you're <strong>unsatisfied</strong> with any Atlassian Cloud product or service, we offer a refund. Monthly subscriptions are eligible for a refund within your first paid month and after your trial period ends. Annual subscriptions are eligible for a refund within 30 days of payment. <refund>See how</refund> to request a refund.</p>"
        },
        premiumOverStandardHeader: {
            id: "ConfluencePricingPage.cloud.premiumOverStandardHeader",
            defaultMessage: "Choosing Premium Plan over Standard"
        },
        premiumOverStandardContent: {
            id: "ConfluencePricingPage.cloud.premiumOverStandardContent",
            defaultMessage:
                "<p>For organizations who need confidence in their ability to scale, the <strong>Premium</strong> plan offers 24/7 Premium Support with 1-hour response times for critical issues, unlimited storage, and a 99.9% uptime SLA financially backed by service credits.</p> <p><premium>Confluence Cloud Premium</premium> also provides an advanced toolkit to help admins better manage their Confluence site with more visibility and control.</p> <p>New product features give administrators temporary access to restricted pages to resolve permissions issues fast. Analytics also offers deeper insights about your team’s site, space, and page use for better reporting, resource decisions, and content.</p>"
        },
        cloudSelfManagedHeader: {
            id: "ConfluencePricingPage.cloud.cloudSelfManagedHeader",
            defaultMessage: "Cloud and self-managed options"
        },
        cloudSelfManagedContent: {
            id: "ConfluencePricingPage.cloud.cloudSelfManagedContent",
            defaultMessage:
                "<p><strong>Important notice:</strong> We've ended sales for new server licenses and will end support for server on Feb 15, 2024, PT. We’re continuing investment in Data Center with several key improvements. <journeyToCloud>Learn what this means for you.</journeyToCloud></p><p>Our <strong>cloud</strong> products provide a complete SaaS solution, ready-made for all Atlassian customers. With cloud, we do the heavy lifting for you by providing built-in platform security and compliance, quick and simple set up, and financially backed SLAs for uptime and performance. Cloud comes with the added benefit of continuous innovation so your end-users will always have the latest and greatest features and functionality.</p> <p>Our <strong>Data Center (or, “self-managed”)</strong> products enable you the flexibility to deploy on an infrastructure of your choice. You’ll have control over data management, security and compliance, when you upgrade, and how you manage uptime and performance.</p> <p><compareCloud>Learn more about the differences</compareCloud>, or <migrationAssessment>take our assessment</migrationAssessment> to see which option best meets your organization’s needs.</p>"
        },
        atlassianGuardHeader: {
            id: "ConfluencePricingPageTeam24.moreDetails.atlassianGuard.title",
            defaultMessage: "Atlassian Guard and Premium plan"
        },
        atlassianGuardContent: {
            id: "ConfluencePricingPageTeam24.moreDetails.atlassianGuard.body",
            defaultMessage:
                "<p><premium>Confluence Cloud Premium</premium> lets teams confidently scale with automation, a 99.9% uptime SLA, unlimited storage, Premium Support, and more features coming soon.</p> <p>For security-conscious organizations that need more visibility and flexible controls in Atlassian cloud, <atlassianGuardStandard>Atlassian Guard Standard</atlassianGuardStandard> helps secure and manage users. You can automate manual processes and introduce security guardrails to reduce risk and free up time.</p> <p>For highly complex or regulated organizations with needs that go beyond centralized visibility and proactive controls, <atlassianGuardPremium>Atlassian Guard Premium</atlassianGuardPremium> offers the granularity to classify your organization’s data, alerts you of anomalous activity, and helps you investigate potential threats across Atlassian cloud.</p> <p>When you layer Atlassian Guard on top of our Premium plan, you’ll get an enterprise-grade solution that lets you scale and secure Atlassian cloud content and users across your entire organization.</p>"
        }
    });

    const faqAccordionsFeatures: Feature[] = [
        {
            header: intl.formatMessage(messages.calculateCostHeader),
            content: intl.formatMessage(messages.calculateCostContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.aboutBillingHeader),
            content: intl.formatMessage(messages.aboutBillingContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.paymentHeader),
            content: intl.formatMessage(messages.paymentContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.freeTrialPeriodHeader),
            content: intl.formatMessage(messages.freeTrialPeriodContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.premiumOverStandardHeader),
            content: intl.formatMessage(messages.premiumOverStandardContent, {
                ...sharedRichtextValues
            })
        },

        {
            header: intl.formatMessage(messages.cloudSelfManagedHeader),
            content: intl.formatMessage(messages.cloudSelfManagedContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.atlassianGuardHeader),
            content: intl.formatMessage(messages.atlassianGuardContent, {
                ...sharedRichtextValues
            })
        }
    ];

    return (
        <FaqAccordions
            header={intl.formatMessage(messages.faqHeader)}
            features={faqAccordionsFeatures}
        />
    );
};

export const DataCenterPricingFaq = () => {
    const intl = useIntl();
    const messages = defineMessages({
        faqHeader: {
            id: "ConfluencePricingPage.datacenter.faqHeader",
            defaultMessage: "Get more details"
        },
        userDefinitionHeader: {
            id: "ConfluencePricingPage.datacenter.userDefinitionHeader",
            defaultMessage: "Definition of user"
        },
        userDefinitionContent: {
            id: "ConfluencePricingPage.datacenter.userDefinitionContent",
            defaultMessage:
                "<p>A <strong>user</strong> is someone who can log in to one of your Confluence sites and who exists in User Management. You can add and remove users as your team changes.</p> <p>Anonymous users may also be granted access and don’t count toward license totals.</p>"
        },
        paymentHeader: {
            id: "ConfluencePricingPage.datacenter.paymentHeader",
            defaultMessage: "Payment options"
        },
        paymentContent: {
            id: "ConfluencePricingPage.datacenter.paymentContent",
            defaultMessage:
                "<p>You can pay for self-managed (also known as “Data Center”) licenses by credit card, bank transfer, check, or ACH (US banks only).</p> <p>Data Center is offered as an annual subscription that includes updates and support as long as your subscription is active.</p>"
        },
        trialAndSubscriptionHeader: {
            id: "ConfluencePricingPage.datacenter.trialAndSubscriptionHeader",
            defaultMessage: "Free trial & subscription info"
        },
        trialAndSubscriptionContent: {
            id: "ConfluencePricingPage.datacenter.trialAndSubscriptionContent",
            defaultMessage:
                "<p>You can host a fully functional copy of Confluence on your own hardware free for 30 days. <downloadConfluence>Download Confluence</downloadConfluence> for Data Center to get started.</p> <p>If you want to keep using Confluence after your 30-day free trial, simply update your existing subscription with a purchased license key. If you need a license key, <purchaseConfluenceDataCenter>purchase your subscription</purchaseConfluenceDataCenter>.</p><p>If you're unsatisfied with any Atlassian product or service, we offer a 30–day money–back guarantee.</p>"
        },
        discountsHeader: {
            id: "ConfluencePricingPage.datacenter.discountsHeader",
            defaultMessage: "About Data Center discounts"
        },
        discountsContent: {
            id: "ConfluencePricingPage.datacenter.discountsContent",
            defaultMessage:
                "<p>Depending on your organization’s situation, you may qualify for one of the following discounts:</p><ul><li><strong>Academic Data Center subscriptions:</strong> Qualified academic institutions receive a 50% discount on all Data Center subscriptions. <licenseDatacenter>Learn more.</licenseDatacenter></li> <li><strong>Community Data Center subscriptions:</strong> Qualified non-profit organizations receive free Data Center subscriptions (inclusive of both products and Marketplace apps). <communityLicense>Learn more.</communityLicense></li></ul>"
        },
        cloudAndSelfManagedHeader: {
            id: "ConfluencePricingPage.datacenter.cloudAndSelfManagedHeader",
            defaultMessage: "About cloud and self-managed options"
        },
        cloudAndSelfManagedContent: {
            id: "ConfluencePricingPage.datacenter.cloudAndSelfManagedContent",
            defaultMessage:
                "<p><strong>Important notice:</strong> We’ve ended sales for new server licenses and will end support for server on February 15, 2024 PT. We’re continuing investment in Data Center with several key improvements. <journeyToCloud>Learn what this means for you</journeyToCloud>.</p> <p>Our <strong>cloud</strong> products provide a complete SaaS solution, ready-made for all Atlassian customers. With cloud, we do the heavy lifting for you by providing built-in platform security and compliance, quick and simple set up, and financially backed SLAs for uptime and performance. Cloud comes with the added benefit of continuous innovation so your end-users will always have the latest and greatest features and functionality.</p> <p>Our <strong>Data Center</strong> products enable you the flexibility to deploy on an infrastructure of your choice. You’ll have control over data management, security and compliance, when you upgrade, and how you manage uptime and performance.</p> <p><compareCloud>Learn more about the differences</compareCloud>, or <migrationAssessment>take our assessment</migrationAssessment> to which option is best for your organization’s needs.</p>"
        }
    });

    const faqAccordionsFeatures: Feature[] = [
        {
            header: intl.formatMessage(messages.userDefinitionHeader),
            content: intl.formatMessage(messages.userDefinitionContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.paymentHeader),
            content: intl.formatMessage(messages.paymentContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.trialAndSubscriptionHeader),
            content: intl.formatMessage(messages.trialAndSubscriptionContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.discountsHeader),
            content: intl.formatMessage(messages.discountsContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.cloudAndSelfManagedHeader),
            content: intl.formatMessage(messages.cloudAndSelfManagedContent, {
                ...sharedRichtextValues
            })
        }
    ];

    return (
        <FaqAccordions
            header={intl.formatMessage(messages.faqHeader)}
            features={faqAccordionsFeatures}
        />
    );
};
